import axios from 'axios';
import type { TFunction } from 'i18next';
import type { AudioKind, ProjectAssetAudio, UserAssetConstraints } from '../services/projectAssets';

export function fileExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot < 0 ? '' : fileName.slice(dot).toLowerCase();
}

export function isAudioFile(file: File, constraints: UserAssetConstraints | null): boolean {
  if (!constraints) return false;
  const extension = fileExtension(file.name);
  return constraints.audio.sourceFileExtensions.some((allowed) => allowed.toLowerCase() === extension);
}

export function isFontFile(file: File): boolean {
  return /\.(ttf|otf)$/i.test(file.name);
}

export function isUploadTooLarge(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 413;
}

export function preselectAudioKind(seconds: number | null, constraints: UserAssetConstraints): AudioKind | null {
  if (seconds === null) return null;
  if (seconds <= constraints.audio.maxSoundEffectSeconds) return 'SoundEffect';
  if (seconds >= constraints.audio.minMusicSeconds) return 'Music';
  return null;
}

export function formatClockTime(seconds: number): string {
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export function formatAudioLimit(seconds: number): string {
  return seconds >= 60 && seconds % 60 === 0 ? `${seconds / 60} min` : `${seconds} s`;
}

export function formatAudioSummary(audio: ProjectAssetAudio, t: TFunction): string {
  const parts =
    audio.kind === 'Music'
      ? [t('assets.audio.music', 'Music'), formatClockTime(audio.durationSeconds)]
      : [t('assets.audio.soundEffect', 'Sound effect'), `${audio.durationSeconds.toFixed(1)} s`];
  if (audio.loopable) parts.push(t('assets.audio.loops', 'loops'));
  return parts.join(' · ');
}
